import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Calendar, Paperclip, FileText, Image, MessageSquare, BarChart2, Inbox, Check, X, CheckCircle, XCircle, AlertTriangle, Clock, Stethoscope, Umbrella } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import LienFichierSecurise from '../components/LienFichierSecurise';
import { BandeauInfo, Bouton, formaterDate } from '../design/composants';
// Suivi « prochain acteur » : objet `suivi` du serveur (vue salarié : /api/moi/demandes ; gestion : /api/conges/a-traiter).
import { SuiviDemande, LigneSuivi, useSuiviMesDemandes, suiviEnCours } from '../components/suivi/SuiviDemande';

// ─── Palette ────────────────────────────────────────────────────────
const VERT   = '#1B5E20';
const ROUGE  = '#C62828';
const ORANGE = '#E65100';
const BLEU   = '#1565C0';
const GRIS   = '#E0E0E0';

// ─── Helpers ────────────────────────────────────────────────────────
// Libellés d'affichage. La liste des types PROPOSÉS vient de l'API (/api/conges/types, référentiel
// types_absence) ; « maladie » ne sert plus qu'à lire les anciennes demandes (l'arrêt se déclare).
const TYPES_LABEL = {
  annuel:       'Congé Annuel',
  maladie:      'Congé Maladie',
  sans_solde:   'Sans Solde',
  exceptionnel: 'Exceptionnel',
  maternite:    'Congé de maternité',
  paternite:    'Congé de paternité',
  conge:        'Congé',
  // C0 (données de santé) : ce que voit le responsable d'un congé maladie, sans motif ni pièce.
  absence_declaree: 'Absence déclarée',
};

const STATUT_CFG = {
  en_attente:     { label: 'En attente',  chipLabel: 'En attente',  bg: '#FFF3E0', color: '#E65100', dot: '#E65100' },
  validee_n1:     { label: 'Validé par votre responsable — En attente RH', chipLabel: 'Attente RH', bg: '#E3F2FD', color: '#1565C0', dot: '#1976D2' },
  validee_finale: { label: 'Approuvé',    chipLabel: 'Approuvé',    bg: '#E8F5E9', color: '#1B5E20', dot: '#43A047' },
  refusee:        { label: 'Refusé',      chipLabel: 'Refusé',      bg: '#FFEBEE', color: '#C62828', dot: '#E53935' },
  annulation_demandee: { label: 'Annulation demandée — en attente RH', chipLabel: 'Annulation demandée', bg: '#FFF8E1', color: '#8D6E00', dot: '#F9A825' },
  annulee:        { label: 'Annulé',      chipLabel: 'Annulé',      bg: '#F5F5F5', color: '#616161', dot: '#9E9E9E' },
  interrompue:    { label: 'Interrompu',  chipLabel: 'Interrompu',  bg: '#F3E5F5', color: '#6A1B9A', dot: '#8E24AA' },
};
const STATUTS_COMPTEURS = ['en_attente', 'validee_n1', 'validee_finale', 'refusee'];
const nbJ = (n) => { const v = Math.round(Number(n || 0) * 100) / 100; return `${String(v).replace('.', ',')} j`; };
const aujourdHui = () => new Date().toISOString().slice(0, 10);
const joursConge = (c) => (c.jours_decomptes !== undefined && c.jours_decomptes !== null ? c.jours_decomptes : c.duree_jours);

const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }) : '–';
const fmtNom = (e) => e ? `${e.prenom} ${e.nom}` : '–';
const inp = { width:'100%', padding:'9px 12px', border:`1px solid ${GRIS}`, borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box', outline:'none' };

// ─── Badge statut ───────────────────────────────────────────────────
function BadgeStatut({ statut }) {
  const cfg = STATUT_CFG[statut] || STATUT_CFG.en_attente;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:cfg.bg, color:cfg.color, borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:cfg.dot, display:'inline-block' }} />
      {cfg.label}
    </span>
  );
}

// ─── Droits à congé par période (Lot 3) : tuiles simples + « Voir le calcul » ──
// Quand la RH doit encore confirmer les droits (a_verifier), on n'affiche jamais « 0 » : « À confirmer ».
function TuileDroit({ libelle, valeur, detail, couleur = '#333', mise }) {
  return (
    <div style={{ background: mise ? '#E8F5E9' : '#FAFAFA', border:`1px solid ${mise ? '#A5D6A7' : GRIS}`, borderRadius:10, padding:'10px 12px', minWidth:0 }}>
      <div style={{ fontSize:12, color:'#555', fontWeight:600 }}>{libelle}</div>
      <div style={{ fontSize:20, fontWeight:800, color:couleur, marginTop:2 }}>{valeur}</div>
      {detail && <div style={{ fontSize:12, color:'#555', marginTop:2 }}>{detail}</div>}
    </div>
  );
}

function BlocDroits({ droits: d }) {
  const [voirCalcul, setVoirCalcul] = useState(false);
  // Recette scénario 1 : « droits établis » (ouverts, repris, confirmés) ≠ « acquisition à vérifier ».
  // Un droit établi s'affiche toujours en chiffres ; seule l'acquisition en cours peut rester « À confirmer ».
  const etablis = d.droits_etablis !== undefined ? d.droits_etablis === true : d.a_verifier !== true;
  const nombre = (v, { reserve = false } = {}) => {
    const x = Number(v);
    if (v === null || v === undefined || Number.isNaN(x)) return reserve || !etablis ? 'À confirmer' : '–';
    return nbJ(x);
  };
  const date = (v) => formaterDate(v) || null;
  const lignes = Array.isArray(d.calcul_lignes) ? d.calcul_lignes : (Array.isArray(d.calcul) ? d.calcul.map((texte) => ({ texte })) : []);
  const ouverture = date(d.ouverture_prevue);
  const depart = date(d.prochain_depart);
  const attente = Number(d.en_attente) || 0;
  const echeance = date(d.prochaine_echeance);
  const reste = d.restants !== undefined ? d.restants : d.disponibles;
  const phrase = d.phrase || (etablis ? null : 'Vos droits sont en cours de vérification par la RH.');
  const allerDemande = (id) => {
    const el = typeof document !== 'undefined' ? document.getElementById(`demande-${id}`) : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  return (
    <div>
      {phrase && <BandeauInfo ton={etablis ? 'info' : 'alerte'}>{phrase}</BandeauInfo>}
      <div role="list" aria-label="Vos jours de congé" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:8 }}>
        <div role="listitem"><TuileDroit libelle="Il vous reste" valeur={etablis ? nombre(reste) : 'À confirmer'} couleur={VERT} mise
          detail={etablis && Number(d.droits_ouverts) > 0 ? `sur ${nbJ(d.droits_ouverts)} de droit` : null} /></div>
        <div role="listitem"><TuileDroit libelle="Planifiés" valeur={nombre(d.planifies)}
          detail={[depart ? `prochain départ le ${depart}` : null, attente > 0 ? `+ ${nbJ(attente)} en attente de réponse` : null].filter(Boolean).join(' · ') || null} /></div>
        <div role="listitem"><TuileDroit libelle="Déjà pris" valeur={nombre(d.deja_pris)} /></div>
        <div role="listitem"><TuileDroit libelle="En cours d'acquisition" valeur={nombre(d.en_acquisition, { reserve: d.acquisition_a_verifier !== false })}
          detail={ouverture && d.acquisition_a_verifier === false ? `utilisable à partir du ${ouverture}` : (d.acquisition_a_verifier !== false ? 'confirmé plus tard par la RH' : null)} /></div>
        <div role="listitem"><TuileDroit libelle="Prochaine échéance" valeur={echeance || (etablis ? 'Aucune' : 'À confirmer')}
          detail={echeance ? 'date limite pour prendre vos jours' : null} /></div>
      </div>
      {lignes.length > 0 && (
        <div style={{ marginTop:10 }}>
          <Bouton variante="lien" petit aria-expanded={voirCalcul} aria-controls="conges-detail-calcul" onClick={() => setVoirCalcul((v) => !v)}>
            {voirCalcul ? 'Masquer le calcul' : 'Voir le calcul'}
          </Bouton>
          {voirCalcul && (
            <ul id="conges-detail-calcul" style={{ margin:'8px 0 0', paddingLeft:18, fontSize:14, color:'#333', lineHeight:1.7 }}>
              {lignes.map((l, i) => (
                <li key={i}>
                  {l.texte}
                  {l.conge_id && <> {' '}<Bouton variante="lien" petit onClick={() => allerDemande(l.conge_id)}>Voir la demande</Bouton></>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Carte du solde : registre des mouvements (acquis − pris − réservé = disponible) ──
// Lot 3 : si le serveur fournit `droits`, on affiche les droits par période ; sinon (null), le rendu historique.
function CarteSolde({ solde }) {
  const [voirMouvements, setVoirMouvements] = useState(false);
  if (!solde) return null;
  if (solde.droits && typeof solde.droits === 'object') {
    return (
      <div style={{ background:'#fff', borderRadius:14, padding:'16px 18px', boxShadow:'0 1px 6px rgba(0,0,0,0.07)', borderTop:`3px solid ${VERT}` }}>
        <p style={{ margin:'0 0 10px', fontSize:11, color:'#555', fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>Vos congés</p>
        <BlocDroits droits={solde.droits} />
      </div>
    );
  }
  const acquis = solde.acquis ?? solde.solde_annuel;
  const pris = solde.pris ?? solde.jours_pris;
  const reserve = solde.reserve ?? solde.jours_en_attente ?? 0;
  const disponible = solde.disponible ?? solde.solde_disponible;
  const pct = acquis > 0 ? Math.round((pris / acquis) * 100) : 0;
  const mois = new Date().getMonth() + 1; // 1-12
  const nonRepris = solde.initialise === false;
  const showAlerteFin = !nonRepris && mois >= 11 && disponible > 5;
  const showAlerteMin = !nonRepris && disponible <= 5 && disponible > 0;
  const mouvements = solde.mouvements || [];

  const compteur = (valeur, libelle, couleur, bordure) => (
    <div style={{ textAlign:'center', ...(bordure ? { borderLeft:`1px solid ${GRIS}` } : {}) }}>
      <div style={{ fontSize:24, fontWeight:800, color:couleur }}>{nbJ(valeur).replace(' j', '')}</div>
      <div style={{ fontSize:10, color:'#888', fontWeight:600 }}>{libelle}</div>
    </div>
  );

  return (
    <div>
      {showAlerteFin && (
        <div style={{ background:'#FFF8E1', border:'1px solid #FFE082', borderRadius:10, padding:'10px 14px', marginBottom:10, display:'flex', alignItems:'center', gap:10 }}>
          <Clock size={20} color="#E65100" style={{ flexShrink:0 }} />
          <div>
            <p style={{ margin:0, fontSize:13, fontWeight:700, color:'#E65100' }}>Fin d'année approche !</p>
            <p style={{ margin:0, fontSize:12, color:'#888' }}>Il vous reste <strong>{nbJ(disponible)}</strong> à poser.</p>
          </div>
        </div>
      )}
      {showAlerteMin && (
        <div style={{ background:'#FFF3E0', border:'1px solid #FFE0B2', borderRadius:10, padding:'10px 14px', marginBottom:10, display:'flex', alignItems:'center', gap:10 }}>
          <p style={{ margin:0, fontSize:13, color:ORANGE }}>Il vous reste <strong>{nbJ(disponible)}</strong> de congés disponibles.</p>
        </div>
      )}

      <div style={{ background:'#fff', borderRadius:14, padding:'16px 18px', boxShadow:'0 1px 6px rgba(0,0,0,0.07)', borderTop:`3px solid ${VERT}` }}>
        <p style={{ margin:'0 0 10px', fontSize:11, color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>Votre solde de congé annuel</p>
        {nonRepris && (
          <p style={{ margin:0, fontSize:13, color:ORANGE }}>
            Votre solde n'a pas encore été repris dans le registre des congés. Vos demandes restent possibles : la RH vérifiera votre solde avant de les approuver.
          </p>
        )}
        {!nonRepris && (<>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:12 }}>
          {compteur(acquis, 'acquis', '#555', false)}
          {compteur(pris, 'pris', '#555', true)}
          {compteur(reserve, 'réservé', reserve > 0 ? ORANGE : '#bbb', true)}
          {compteur(disponible, 'disponible', VERT, true)}
        </div>
        <div style={{ background:'#f0f0f0', borderRadius:4, height:6, overflow:'hidden' }}>
          <div style={{ background: pct > 80 ? ROUGE : VERT, borderRadius:4, height:6, width:`${Math.min(100, Math.max(0, pct))}%`, transition:'width 0.4s' }} />
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:10, color:'#bbb', marginTop:6 }}>
          <span>Disponible = acquis − pris − réservé (demandes en cours)</span>
          {mouvements.length > 0 && (
            <button onClick={() => setVoirMouvements((v) => !v)} style={{ background:'none', border:'none', color:BLEU, fontSize:11, cursor:'pointer', padding:0 }}>
              {voirMouvements ? 'Masquer les mouvements' : `Voir les mouvements (${mouvements.length})`}
            </button>
          )}
        </div>
        {voirMouvements && (
          <div style={{ marginTop:10, borderTop:`1px solid ${GRIS}`, paddingTop:8 }}>
            {mouvements.map((m) => (
              <div key={m.id} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'4px 0', color:'#555' }}>
                <span>{fmt(m.date_valeur)} — {m.libelle}{m.periode ? ` (${m.periode})` : ''}</span>
                <strong style={{ color: m.jours < 0 ? ROUGE : VERT }}>{m.jours > 0 ? '+' : ''}{nbJ(m.jours)}</strong>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}

// ─── Modale de demande de congé ─────────────────────────────────────
// Types proposés par l'API (types_absence) ; demi-journée si le type l'autorise ; avant l'envoi,
// la simulation du serveur affiche « Disponible 16 j · cette demande 5 j · il vous restera 11 j » et
// « Votre demande sera envoyée à M. Gueye (Chef comptable) ».
function ModaleDemandeConge({ onClose, onSuccess }) {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ type_conge:'annuel', date_debut:'', date_fin:'', motif:'', demi_journee_debut:'', demi_journee_fin:'' });
  const [fichier, setFichier] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [erreurSimulation, setErreurSimulation] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get('/api/conges/types').then(({ data }) => setTypes(data?.data || [])).catch(() => setTypes([]));
  }, []);

  const type = types.find((t) => t.type_conge === form.type_conge);
  const motifObligatoire = type ? type.motif_obligatoire : ['exceptionnel','sans_solde'].includes(form.type_conge);
  const demiJournee = !!(type && type.demi_journee);
  const documentOptionnel = form.type_conge === 'exceptionnel';

  useEffect(() => {
    setSimulation(null); setErreurSimulation('');
    if (!form.date_debut || !form.date_fin || form.date_fin < form.date_debut) return undefined;
    const minuteur = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ type_conge: form.type_conge, date_debut: form.date_debut, date_fin: form.date_fin });
        if (demiJournee && form.demi_journee_debut) params.set('demi_journee_debut', form.demi_journee_debut);
        if (demiJournee && form.demi_journee_fin) params.set('demi_journee_fin', form.demi_journee_fin);
        const { data } = await axios.get(`/api/conges/simulation?${params.toString()}`);
        setSimulation(data?.data || null);
      } catch (e) { setErreurSimulation(e.response?.data?.message || ''); }
    }, 300);
    return () => clearTimeout(minuteur);
  }, [form.type_conge, form.date_debut, form.date_fin, form.demi_journee_debut, form.demi_journee_fin, demiJournee]);

  const soumettre = async () => {
    setErr('');
    if (!form.date_debut || !form.date_fin) return setErr('Dates requises');
    if (form.date_fin < form.date_debut) return setErr('La date de fin doit être après la date de début');
    if (motifObligatoire && form.motif.trim().length < 10) return setErr('Motif obligatoire (min. 10 caractères)');

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('type_conge', form.type_conge);
      fd.append('date_debut', form.date_debut);
      fd.append('date_fin', form.date_fin);
      if (demiJournee && form.demi_journee_debut) fd.append('demi_journee_debut', form.demi_journee_debut);
      if (demiJournee && form.demi_journee_fin) fd.append('demi_journee_fin', form.demi_journee_fin);
      if (form.motif) fd.append('motif', form.motif);
      if (fichier) fd.append('document', fichier);
      const { data } = await axios.post('/api/conges', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // On dit précisément à qui la demande a été transmise (responsable, délégué, N+2 ou RH).
      onSuccess((data?.envoyee_a || 'Votre demande a été envoyée.').replace('sera envoyée', 'a été envoyée'));
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || 'Erreur lors de la soumission');
    } finally { setLoading(false); }
  };

  const lbl = { fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:5 };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:500, boxShadow:'0 8px 40px rgba(0,0,0,0.18)', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:'#1a1a1a' }}>Nouvelle demande de congé</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#888', lineHeight:1 }}>×</button>
        </div>

        {err && <div style={{ background:'#FFEBEE', color:ROUGE, borderRadius:8, padding:'10px 14px', fontSize:13, marginBottom:14 }}>{err}</div>}

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={lbl}>Type de congé</label>
            <select value={form.type_conge} onChange={e => setForm({ ...form, type_conge:e.target.value, motif:'', demi_journee_debut:'', demi_journee_fin:'' })} style={inp}>
              {(types.length ? types.map((t) => [t.type_conge, t.libelle]) : [['annuel', TYPES_LABEL.annuel]]).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <p style={{ margin:'6px 0 0', fontSize:11.5, color:'#666', display:'flex', alignItems:'center', gap:5 }}>
              <Stethoscope size={12} />Arrêt maladie : il ne se demande pas comme un congé, il se déclare (dates et certificat, sans motif médical).
            </p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={lbl}>Date début</label>
              <input type="date" value={form.date_debut} onChange={e => setForm({ ...form, date_debut:e.target.value })} style={inp} />
            </div>
            <div>
              <label style={lbl}>Date fin</label>
              <input type="date" value={form.date_fin} min={form.date_debut} onChange={e => setForm({ ...form, date_fin:e.target.value })} style={inp} />
            </div>
          </div>

          {demiJournee && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={lbl}>Premier jour</label>
                <select value={form.demi_journee_debut} onChange={e => setForm({ ...form, demi_journee_debut:e.target.value })} style={inp}>
                  <option value="">Journée entière</option>
                  <option value="APRES_MIDI">À partir de l'après-midi</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Dernier jour</label>
                <select value={form.demi_journee_fin} onChange={e => setForm({ ...form, demi_journee_fin:e.target.value })} style={inp}>
                  <option value="">Journée entière</option>
                  <option value="MATIN">Jusqu'à midi (matin seulement)</option>
                </select>
              </div>
            </div>
          )}

          {simulation && (
            <div style={{ background: simulation.suffisant === false ? '#FFEBEE' : '#F1F8E9', borderRadius:8, padding:'10px 14px', display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:13, color: simulation.suffisant === false ? ROUGE : VERT, fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
                <Calendar size={16} style={{ flexShrink:0 }} />{simulation.phrase}
              </span>
              <span style={{ fontSize:12, color:'#555' }}>{simulation.envoyee_a}</span>
            </div>
          )}
          {erreurSimulation && <p style={{ margin:0, fontSize:12, color:ORANGE }}>{erreurSimulation}</p>}

          <div>
            <label style={lbl}>
              Motif {motifObligatoire ? <span style={{ color:ROUGE }}>* obligatoire</span> : <span style={{ color:'#aaa' }}>(optionnel)</span>}
            </label>
            <textarea
              value={form.motif}
              onChange={e => setForm({ ...form, motif:e.target.value })}
              placeholder={
                form.type_conge === 'exceptionnel' ? 'Décrivez la circonstance exceptionnelle (mariage, décès, naissance...)...' :
                form.type_conge === 'sans_solde'   ? 'Précisez la raison du congé sans solde...' :
                'Précisez le motif de votre demande (optionnel)...'
              }
              style={{ ...inp, height:80, resize:'vertical' }}
            />
            {motifObligatoire && form.motif.length > 0 && form.motif.length < 10 && (
              <p style={{ margin:'4px 0 0', fontSize:11, color:ORANGE }}>{10 - form.motif.length} caractère(s) manquant(s)</p>
            )}
          </div>

          {documentOptionnel && (
            <div>
              <label style={lbl}><span>Pièce justificative <span style={{ color:'#aaa' }}>(optionnelle)</span></span></label>
              <div style={{ border:`2px dashed ${fichier ? VERT : GRIS}`, borderRadius:10, padding:'14px 16px', textAlign:'center', cursor:'pointer', background: fichier ? '#F1F8E9' : '#fafafa', transition:'all 0.2s' }}
                onClick={() => document.getElementById('inp-fichier').click()}>
                <input id="inp-fichier" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }}
                  onChange={e => setFichier(e.target.files[0] || null)} />
                {fichier ? (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    {fichier.name.endsWith('.pdf') ? <FileText size={20} color={VERT} /> : <Image size={20} color={VERT} />}
                    <div style={{ textAlign:'left' }}>
                      <p style={{ margin:0, fontSize:13, fontWeight:600, color:VERT }}>{fichier.name}</p>
                      <p style={{ margin:0, fontSize:11, color:'#888' }}>{(fichier.size / 1024).toFixed(0)} Ko</p>
                    </div>
                    <button onClick={e => { e.stopPropagation(); setFichier(null); }}
                      style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#bbb', fontSize:18 }}>×</button>
                  </div>
                ) : (
                  <div>
                    <Paperclip size={22} color="#bbb" style={{ marginBottom:4 }} />
                    <p style={{ margin:0, fontSize:13, color:'#888' }}>Cliquer pour sélectionner un fichier</p>
                    <p style={{ margin:'3px 0 0', fontSize:11, color:'#bbb' }}>PDF, JPG ou PNG — 5 Mo max</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display:'flex', gap:10, marginTop:4 }}>
            <button onClick={onClose} style={{ flex:1, padding:'11px', borderRadius:9, border:`1px solid ${GRIS}`, background:'#fff', color:'#555', fontWeight:600, fontSize:13, cursor:'pointer' }}>
              Annuler
            </button>
            <button onClick={soumettre} disabled={loading || (simulation && simulation.suffisant === false)}
              style={{ flex:2, padding:'11px', borderRadius:9, border:'none', background:loading ? '#aaa' : VERT, color:'#fff', fontWeight:700, fontSize:13, cursor:loading ? 'default' : 'pointer' }}>
              {loading ? 'Envoi...' : 'Envoyer la demande'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modale d'action : commentaire ou motif obligatoire, date de reprise (interruption) ──
const CONFIG_ACTIONS = {
  'valider-n1':         { titre: 'Valider la demande (étape 1)', placeholder: 'Commentaire (optionnel)...' },
  'valider-final':      { titre: 'Approuver le congé (étape 2)', placeholder: 'Commentaire (optionnel)...' },
  'valider-final-solde': { titre: 'Approuver — solde à vérifier', obligatoire: true, min: 10,
    aide: 'Le solde de ce salarié n\'a pas encore été repris dans le registre. En approuvant, vous confirmez l\'avoir vérifié. Indiquez comment (dossier, bulletin, attestation…) : cette confirmation est tracée.',
    placeholder: 'Exemple : dossier papier vérifié, 18 jours restants au 30/09' },
  'refuser':            { titre: 'Motif du refus', placeholder: 'Expliquez la raison du refus (communiquée au salarié)...', obligatoire: true },
  'annuler-rh':         { titre: 'Motif de l\'annulation', placeholder: 'Raison de l\'annulation (les jours pris sont rendus au solde)...', obligatoire: true },
  'refuser-annulation': { titre: 'Refuser la demande d\'annulation', placeholder: 'Motif communiqué au salarié...', obligatoire: true },
  'interruption':       { titre: 'Interruption du congé (retour anticipé)', placeholder: 'Motif (rappel, retour anticipé)...', obligatoire: true, avecDate: true },
  'demande-annulation': { titre: 'Demander l\'annulation de ce congé', placeholder: 'Motif (facultatif)...' },
};

function ModaleAction({ titre, placeholder, obligatoire, min = 3, aide, avecDate, conge, onConfirm, onClose }) {
  const [motif, setMotif] = useState('');
  const [date, setDate] = useState('');
  const [err, setErr] = useState('');
  const confirmer = () => {
    if (obligatoire && motif.trim().length < min) return setErr(min > 3 ? `Ce texte est obligatoire (${min} caractères au moins).` : 'Le motif est obligatoire.');
    if (avecDate && !date) return setErr('Indiquez la date de reprise du travail.');
    onConfirm({ motif: motif.trim(), date });
    return undefined;
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, padding:24, width:'100%', maxWidth:420, boxShadow:'0 8px 40px rgba(0,0,0,0.18)' }}>
        <h3 style={{ margin:'0 0 14px', fontSize:15, fontWeight:700 }}>{titre}</h3>
        {aide && <p style={{ margin:'0 0 12px', fontSize:12.5, color:'#555', lineHeight:1.45 }}>{aide}</p>}
        {err && <div style={{ background:'#FFEBEE', color:ROUGE, borderRadius:8, padding:'8px 12px', fontSize:12.5, marginBottom:10 }}>{err}</div>}
        {avecDate && (
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:5 }}>Date de reprise du travail</label>
            <input type="date" value={date} min={conge?.date_debut} max={conge?.date_fin} onChange={e => setDate(e.target.value)} style={inp} />
            <p style={{ margin:'4px 0 0', fontSize:11, color:'#888' }}>Les jours à partir de cette date sont rendus au solde.</p>
          </div>
        )}
        <textarea value={motif} onChange={e => setMotif(e.target.value)}
          placeholder={placeholder || 'Commentaire (optionnel)...'}
          style={{ ...inp, height:90, resize:'vertical', marginBottom:14 }} />
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'10px', borderRadius:8, border:`1px solid ${GRIS}`, background:'#fff', color:'#555', fontWeight:600, fontSize:13, cursor:'pointer' }}>
            Annuler
          </button>
          <button onClick={confirmer} style={{ flex:2, padding:'10px', borderRadius:8, border:'none', background:VERT, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Historique d'une demande : journal des décisions (qui, quand, commentaire) ──
function ModaleHistorique({ historique, onClose }) {
  const fmtHeure = (d) => (d ? new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '–');
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, padding:24, width:'100%', maxWidth:480, boxShadow:'0 8px 40px rgba(0,0,0,0.18)', maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Historique de la demande</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#888', lineHeight:1 }}>×</button>
        </div>
        {(historique.evenements || []).length === 0 && <p style={{ fontSize:13, color:'#888' }}>Aucun événement enregistré (demande antérieure au journal des décisions).</p>}
        {(historique.evenements || []).map((e, i) => (
          <div key={i} style={{ borderLeft:`3px solid ${VERT}`, padding:'4px 0 8px 12px', marginBottom:6 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#1a1a1a' }}>{e.libelle}</div>
            <div style={{ fontSize:11.5, color:'#888' }}>{fmtHeure(e.le)}{e.acteur ? ` · ${e.acteur}` : ''}</div>
            {e.commentaire && <div style={{ fontSize:12, color:'#555', marginTop:2 }}>{e.commentaire}</div>}
            {e.date_reprise && <div style={{ fontSize:12, color:'#555' }}>Reprise le {fmt(e.date_reprise)}</div>}
          </div>
        ))}
        {(historique.mouvements || []).length > 0 && (
          <div style={{ marginTop:10, borderTop:`1px solid ${GRIS}`, paddingTop:8 }}>
            <p style={{ margin:'0 0 6px', fontSize:12, fontWeight:700, color:'#555' }}>Effet sur le solde</p>
            {historique.mouvements.map((m, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#555' }}>
                <span>{m.libelle} ({m.annee_reference})</span>
                <strong style={{ color: m.jours < 0 ? ROUGE : VERT }}>{m.jours > 0 ? '+' : ''}{nbJ(m.jours)}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ligne de demande ───────────────────────────────────────────────
function LigneDemande({ conge, showEmploye, actions, onHistorique, suivi }) {
  // Justificatif servi par l'API authentifiée (plus d'URL publique).
  const docUrl = conge.document_url || null;

  return (
    <div id={`demande-${conge.id}`} style={{ background:'#fff', borderRadius:12, padding:'14px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', border:`1px solid ${GRIS}`, marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
        <div style={{ flex:1, minWidth:200 }}>
          {showEmploye && (
            <p style={{ margin:'0 0 3px', fontWeight:700, fontSize:14, color:'#1a1a1a' }}>
              {fmtNom(conge.employe)}
              <span style={{ fontWeight:400, color:'#888', fontSize:12, marginLeft:8 }}>{conge.employe?.matricule}</span>
            </p>
          )}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <span style={{ fontWeight:600, fontSize:13, color:VERT }}>{conge.type_libelle || TYPES_LABEL[conge.type_conge] || conge.type_conge}</span>
            <span style={{ fontSize:12, color:'#888' }}>
              {fmt(conge.date_debut)}{conge.demi_journee_debut === 'APRES_MIDI' ? ' (après-midi)' : ''} → {fmt(conge.date_fin)}{conge.demi_journee_fin === 'MATIN' ? ' (matin)' : ''}
              <strong style={{ marginLeft:6, color:'#555' }}>{nbJ(joursConge(conge))}</strong>
              {conge.statut === 'interrompue' && conge.date_fin_effective && <span style={{ marginLeft:6, color:'#6A1B9A' }}>· dernier jour {fmt(conge.date_fin_effective)}</span>}
            </span>
          </div>
          {conge.motif && <p style={{ margin:'4px 0 0', fontSize:12, color:'#666', fontStyle:'italic' }}>"{conge.motif}"</p>}

          {/* Document justificatif */}
          {docUrl && (
            <LienFichierSecurise chemin={docUrl}
              style={{ display:'inline-flex', alignItems:'center', gap:5, marginTop:5, fontSize:11, color:BLEU, textDecoration:'none', background:'#E3F2FD', borderRadius:6, padding:'3px 10px' }}>
              <Paperclip size={11} style={{ marginRight:3 }} />{conge.document_nom || 'Voir le document'}
            </LienFichierSecurise>
          )}

          {/* Commentaires */}
          {conge.commentaire_n1 && conge.statut !== 'en_attente' && (
            <p style={{ margin:'4px 0 0', fontSize:11, color:BLEU, display:'flex', alignItems:'center', gap:4 }}><MessageSquare size={11} />N1 : {conge.commentaire_n1}</p>
          )}
          {conge.commentaire_final && (
            <p style={{ margin:'2px 0 0', fontSize:11, color:conge.statut === 'refusee' ? ROUGE : VERT }}>
              <MessageSquare size={11} style={{ marginRight:3, flexShrink:0 }} />RH : {conge.commentaire_final}
            </p>
          )}
          {/* Prochain acteur : bandeau pour le salarié, ligne « Doit agir » pour le valideur (demande en cours seulement). */}
          {suiviEnCours(suivi) && (showEmploye ? <LigneSuivi suivi={suivi} /> : <SuiviDemande suivi={suivi} />)}
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
          <BadgeStatut statut={conge.statut} />
          {conge.controle_solde === 'SOLDE_A_VERIFIER' && ['en_attente', 'validee_n1'].includes(conge.statut) && (
            <span style={{ fontSize:11, fontWeight:700, color:ORANGE, background:'#FFF3E0', borderRadius:6, padding:'2px 8px' }}>Solde à vérifier</span>
          )}
          {conge.solde_verifie_le && (
            <span style={{ fontSize:11, color:'#888' }} title={conge.solde_verification || ''}>Solde vérifié par la RH le {fmt(conge.solde_verifie_le)}</span>
          )}
          <span style={{ fontSize:11, color:'#bbb' }}>Demandé le {fmt(conge.created_at)}</span>
          {onHistorique && (
            <button onClick={() => onHistorique(conge)} style={{ background:'none', border:'none', color:BLEU, fontSize:11, cursor:'pointer', padding:0, display:'inline-flex', alignItems:'center', gap:4 }}>
              <Clock size={11} />Historique
            </button>
          )}
          {actions && <div style={{ display:'flex', gap:6, marginTop:2, flexWrap:'wrap', justifyContent:'flex-end' }}>{actions}</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard stats RH ────────────────────────────────────────────
function DashboardRH({ stats }) {
  if (!stats) return null;
  return (
    <div style={{ marginBottom:20 }}>
      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        <div style={{ background:'#fff', borderRadius:12, padding:'14px 16px', borderTop:`3px solid ${VERT}`, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:VERT }}>{stats.total_demandes_annee}</p>
          <p style={{ margin:0, fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:0.8 }}>Demandes {stats.annee}</p>
        </div>
        <div style={{ background:'#fff', borderRadius:12, padding:'14px 16px', borderTop:`3px solid ${ORANGE}`, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:ORANGE }}>{stats.total_maladie_annee}</p>
          <p style={{ margin:0, fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:0.8 }}>Congés maladie</p>
        </div>
        <div style={{ background:'#fff', borderRadius:12, padding:'14px 16px', borderTop:`3px solid ${ROUGE}`, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:ROUGE }}>{stats.sans_conge?.length || 0}</p>
          <p style={{ margin:0, fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:0.8 }}>Sans congé pris</p>
        </div>
      </div>

      {/* Employés avec beaucoup de jours restants */}
      {stats.beaucoup_restants?.length > 0 && (
        <div style={{ background:'#fff', borderRadius:12, padding:'16px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', marginBottom:12 }}>
          <p style={{ margin:'0 0 12px', fontWeight:700, fontSize:13, color:'#1a1a1a' }}>
            <span style={{ display:'flex', alignItems:'center', gap:6 }}><BarChart2 size={14} />Employés avec beaucoup de jours restants (≥ 20j)</span>
          </p>
          {stats.beaucoup_restants.slice(0,5).map(e => (
            <div key={e.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:`1px solid ${GRIS}` }}>
              <span style={{ fontSize:13, color:'#555' }}>{e.nom} <span style={{ color:'#bbb', fontSize:11 }}>{e.matricule}</span></span>
              <span style={{ fontSize:13, fontWeight:700, color:VERT }}>{e.jours_restants}j restants</span>
            </div>
          ))}
        </div>
      )}

      {/* Congés maladie : statistique agrégée par service (C0) — aucun nom ; un service n'est
          affiché que s'il compte au moins 5 salariés, pour qu'on ne puisse reconnaître personne. */}
      {stats.maladie_par_service?.services?.length > 0 && (
        <div style={{ background:'#fff', borderRadius:12, padding:'16px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ margin:'0 0 12px', fontWeight:700, fontSize:13, color:'#1a1a1a' }}>
            <span style={{ display:'flex', alignItems:'center', gap:6 }}><Stethoscope size={14} />Congés maladie par service</span>
          </p>
          {stats.maladie_par_service.services.map((g, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:`1px solid ${GRIS}` }}>
              <span style={{ fontSize:13, color:'#555' }}>{g.service} <span style={{ color:'#bbb', fontSize:11 }}>{g.effectif} salariés</span></span>
              <span style={{ fontSize:12, color:ORANGE }}>{g.nb_arrets} arrêt{g.nb_arrets > 1 ? 's' : ''} · {g.jours}j</span>
            </div>
          ))}
          {stats.maladie_par_service.autres_services?.nb_services > 0 && (
            <p style={{ margin:'8px 0 0', fontSize:11, color:'#888' }}>
              {stats.maladie_par_service.autres_services.nb_arrets} arrêt(s) dans {stats.maladie_par_service.autres_services.nb_services} service(s) de moins de {stats.maladie_par_service.seuil_personnes} salariés, non détaillés.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════
export default function CongesPage() {
  const { employe, hasPermission } = useAuth();
  const navigate = useNavigate();
  const role = employe?.role || 'employe';

  const ROLES_RH      = ['admin','drh','rh_principal','assistant_rh','directeur_general','directeur_adjoint'];
  const ROLES_MANAGER = ['prepose_regional','responsable_site','chef_departement','chef_service','chef_equipe'];
  const isRH      = ROLES_RH.includes(role);
  const isManager = ROLES_MANAGER.includes(role) || isRH;
  const estEmploye = role === 'employe';
  const peutValiderConges = isManager || hasPermission('valider_conges_n1');

  const [onglet, setOnglet] = useState(isRH ? 'a_traiter' : 'mes_demandes');
  const [solde, setSolde]           = useState(null);
  const [mesDemandes, setMesDemandes] = useState([]);
  const [aTraiter, setATraiter]     = useState([]);
  const [perimetreATraiter, setPerimetreATraiter] = useState('');
  const [toutes, setToutes]         = useState([]);
  const [stats, setStats]           = useState(null);
  const [filtreStatut, setFiltreStatut] = useState('');
  const [showStats, setShowStats]   = useState(false);
  const [versionSuivi, setVersionSuivi] = useState(0);
  const suivisMoi = useSuiviMesDemandes(versionSuivi > 0, versionSuivi);

  const [modaleNew, setModaleNew]       = useState(false);
  const [modaleAction, setModaleAction] = useState(null);
  const [annulPending, setAnnulPending] = useState(null);
  const [historique, setHistorique]     = useState(null);
  const [toast, setToast]               = useState(null);
  const [loading, setLoading]           = useState(false);

  const showToast = (texte, type='ok') => {
    setToast({ texte, type });
    setTimeout(() => setToast(null), 3500);
  };

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [soldeR, mesR] = await Promise.all([
        axios.get('/api/conges/solde'),
        axios.get('/api/conges/mes-demandes'),
      ]);
      setSolde(soldeR.data);
      setMesDemandes(mesR.data.conges || []);

      if (peutValiderConges) {
        const atr = await axios.get('/api/conges/a-traiter');
        setATraiter(atr.data.conges || []);
        setPerimetreATraiter(atr.data.perimetre || '');
      }
      if (isRH) {
        const params = filtreStatut ? `?statut=${filtreStatut}` : '';
        const tout = await axios.get(`/api/conges/toutes${params}`);
        setToutes(tout.data.conges || []);
      }
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); setVersionSuivi((v) => v + 1); }
  }, [peutValiderConges, isRH, filtreStatut]);

  const chargerStats = useCallback(async () => {
    if (!isRH || stats) return;
    try {
      const r = await axios.get('/api/conges/stats');
      setStats(r.data);
    } catch (e) { console.error(e); }
  }, [isRH, stats]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { if (showStats) chargerStats(); }, [showStats, chargerStats]);

  const annuler = async (id) => {
    try {
      await axios.delete(`/api/conges/${id}`);
      showToast('Demande annulée');
      charger();
    } catch (e) { showToast(e.response?.data?.message || 'Erreur', 'err'); }
  };

  const voirHistorique = async (conge) => {
    try {
      const { data } = await axios.get(`/api/conges/${conge.id}/historique`);
      setHistorique({ conge, ...data.data });
    } catch (e) { showToast(e.response?.data?.message || 'Historique indisponible', 'err'); }
  };

  // Chaque action : méthode, adresse et corps (motif obligatoire pour refus, annulation, interruption).
  const ACTIONS = {
    'valider-n1':         { m: 'patch', url: (id) => `/api/conges/${id}/valider-n1`,         corps: (v) => ({ commentaire: v.motif }), ok: 'Demande validée (étape 1) — la RH est prévenue' },
    'valider-final':      { m: 'patch', url: (id) => `/api/conges/${id}/valider-final`,      corps: (v) => ({ commentaire: v.motif }), ok: 'Congé approuvé — solde et temps mis à jour' },
    'valider-final-solde': { m: 'patch', url: (id) => `/api/conges/${id}/valider-final`,     corps: (v) => ({ confirmer_solde: true, verification_solde: v.motif }), ok: 'Congé approuvé — vérification du solde enregistrée' },
    'refuser':            { m: 'patch', url: (id) => `/api/conges/${id}/refuser`,            corps: (v) => ({ motif: v.motif }), ok: 'Demande refusée' },
    'annuler-rh':         { m: 'patch', url: (id) => `/api/conges/${id}/annuler-rh`,         corps: (v) => ({ motif: v.motif }), ok: 'Congé annulé — jours rendus au solde' },
    'refuser-annulation': { m: 'patch', url: (id) => `/api/conges/${id}/refuser-annulation`, corps: (v) => ({ motif: v.motif }), ok: 'Demande d\'annulation refusée' },
    'interruption':       { m: 'post',  url: (id) => `/api/conges/${id}/interruption`,       corps: (v) => ({ motif: v.motif, date_reprise: v.date }), ok: 'Interruption enregistrée' },
    'demande-annulation': { m: 'post',  url: (id) => `/api/conges/${id}/demande-annulation`, corps: (v) => ({ motif: v.motif }), ok: 'Demande d\'annulation envoyée à la RH' },
  };

  const execAction = async (valeurs) => {
    if (!modaleAction) return;
    const { congeId, action } = modaleAction;
    const a = ACTIONS[action];
    try {
      const { data } = await axios[a.m](a.url(congeId), a.corps(valeurs));
      showToast(data?.demande ? 'Demande d\'interruption transmise à la RH' : (a.ok || 'Action effectuée'));
      setModaleAction(null);
      charger();
      setStats(null); // reset stats cache
    } catch (e) {
      if (action === 'valider-final' && e.response?.data?.code === 'SOLDE_A_VERIFIER') {
        setModaleAction({ ...modaleAction, action: 'valider-final-solde' });
        return;
      }
      showToast(e.response?.data?.message || 'Erreur', 'err');
      setModaleAction(null);
    }
  };

  // ─── Boutons d'action ───────────────────────────────────────────
  const btnV = (conge, action, label) => (
    <button key={action} onClick={() => setModaleAction({ congeId:conge.id, action, conge })}
      style={{ padding:'5px 12px', borderRadius:7, border:`1px solid #C8E6C9`, background:'#E8F5E9', color:VERT, fontWeight:700, fontSize:12, cursor:'pointer', display:'inline-flex', alignItems:'center' }}>
      <Check size={12} style={{ marginRight:4 }} />{label}
    </button>
  );
  const btnR = (conge, action='refuser', label='Refuser') => (
    <button key={action} onClick={() => setModaleAction({ congeId:conge.id, action, conge })}
      style={{ padding:'5px 12px', borderRadius:7, border:`1px solid #FFCDD2`, background:'#FFEBEE', color:ROUGE, fontWeight:700, fontSize:12, cursor:'pointer', display:'inline-flex', alignItems:'center' }}>
      <X size={12} style={{ marginRight:4 }} />{label}
    </button>
  );

  const getActions = (conge) => {
    const { statut } = conge;
    const j = aujourdHui();
    const commence = conge.date_debut <= j;
    const passe = conge.date_fin < j;
    if (isRH) {
      if (statut === 'annulation_demandee') return [btnV(conge, 'annuler-rh', 'Accepter l\'annulation'), btnR(conge, 'refuser-annulation', 'Refuser l\'annulation')];
      if (statut === 'validee_finale' && !passe) return [commence ? btnR(conge, 'interruption', 'Interrompre') : btnR(conge, 'annuler-rh', 'Annuler')];
      const approuver = conge.controle_solde === 'SOLDE_A_VERIFIER' ? btnV(conge, 'valider-final-solde', 'Approuver (solde à vérifier)') : btnV(conge, 'valider-final', 'Approuver');
      if (statut === 'en_attente') return [btnV(conge, 'valider-n1', 'Valider N1'), approuver, btnR(conge)];
      if (statut === 'validee_n1')  return [approuver, btnR(conge)];
      return null;
    }
    if (statut === 'en_attente') return [btnV(conge, 'valider-n1', 'Valider N1'), btnR(conge)];
    return null;
  };

  // Actions du salarié sur ses propres demandes.
  const actionsSalarie = (conge) => {
    const j = aujourdHui();
    const btn = (key, label, onClick) => (
      <button key={key} onClick={onClick}
        style={{ padding:'5px 12px', borderRadius:7, border:'1px solid #FFCDD2', background:'#FFEBEE', color:ROUGE, fontWeight:600, fontSize:12, cursor:'pointer' }}>
        {label}
      </button>
    );
    if (conge.statut === 'en_attente') return [btn('ann', 'Annuler', () => setAnnulPending(conge))];
    if (['validee_n1', 'validee_finale'].includes(conge.statut) && conge.date_debut > j) {
      return [btn('dem', 'Demander l\'annulation', () => setModaleAction({ congeId: conge.id, action: 'demande-annulation', conge }))];
    }
    if (conge.statut === 'validee_finale' && conge.date_debut <= j && conge.date_fin >= j) {
      return [btn('int', 'Signaler un retour anticipé', () => setModaleAction({ congeId: conge.id, action: 'interruption', conge }))];
    }
    return null;
  };

  // ─── Onglets ────────────────────────────────────────────────────
  const nbATraiter = aTraiter.length;
  const onglets = [
    { id:'mes_demandes', label:'Mes demandes',      visible: true },
    { id:'a_traiter',    label:`À valider${nbATraiter > 0 ? ` (${nbATraiter})` : ''}`, visible: peutValiderConges },
    { id:'toutes',       label:'Vue globale',        visible: isRH },
  ].filter(o => o.visible);

  return (
    <Layout>
      <div style={{ fontFamily:'system-ui, -apple-system, sans-serif', minHeight:'100vh', background:'#f7f7f7' }}>

        {/* Toast */}
        {toast && (
          <div style={{ position:'fixed', top:20, right:20, zIndex:2000, background:toast.type==='err' ? '#FFEBEE' : '#E8F5E9', color:toast.type==='err' ? ROUGE : VERT, border:`1px solid ${toast.type==='err' ? '#FFCDD2' : '#C8E6C9'}`, borderRadius:10, padding:'12px 20px', fontSize:13, fontWeight:600, boxShadow:'0 4px 20px rgba(0,0,0,0.12)', maxWidth:360 }}>
            {toast.type==='err' ? <XCircle size={14} style={{ marginRight:6, flexShrink:0 }} /> : <CheckCircle size={14} style={{ marginRight:6, flexShrink:0 }} />}{toast.texte}
          </div>
        )}

        {/* Modale annulation employé */}
        {annulPending && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ background:'#fff', borderRadius:12, padding:'20px 24px', width:420, maxWidth:'90vw', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin:'0 0 14px', fontSize:16, fontWeight:700, color:'#1A1A1A' }}>Annuler cette demande ?</h3>
              <div style={{ background:'#F7F7F7', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#1A1A1A', marginBottom:4 }}>{TYPES_LABEL[annulPending.type_conge] || annulPending.type_conge}</div>
                <div style={{ fontSize:13, color:'#4A4A4A', marginBottom:2 }}>Du {fmt(annulPending.date_debut)} au {fmt(annulPending.date_fin)}</div>
                <div style={{ fontSize:13, color:'#4A4A4A' }}>{nbJ(joursConge(annulPending))}</div>
              </div>
              <p style={{ margin:'0 0 16px', fontSize:13, color:'#666' }}>La demande reste dans votre historique, au statut « Annulé ». Les jours réservés sont libérés.</p>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={() => setAnnulPending(null)} style={{ padding:'9px 18px', borderRadius:8, border:'1px solid #CCC', background:'#F5F5F5', color:'#555', fontSize:13, cursor:'pointer', fontWeight:600, fontFamily:'inherit' }}>Retour</button>
                <button onClick={() => { annuler(annulPending.id); setAnnulPending(null); }} style={{ padding:'9px 18px', borderRadius:8, border:'none', background:ROUGE, color:'#fff', fontSize:13, cursor:'pointer', fontWeight:600, fontFamily:'inherit' }}>Annuler la demande</button>
              </div>
            </div>
          </div>
        )}

        {/* Modales */}
        {modaleNew && <ModaleDemandeConge onClose={() => setModaleNew(false)} onSuccess={(m) => { showToast(m); charger(); }} />}
        {modaleAction && (
          <ModaleAction
            {...CONFIG_ACTIONS[modaleAction.action]}
            conge={modaleAction.conge}
            onConfirm={execAction}
            onClose={() => setModaleAction(null)}
          />
        )}
        {historique && <ModaleHistorique historique={historique} onClose={() => setHistorique(null)} />}

        {/* Header */}
        <div className="sg-bandeau-clair" style={{ background:`linear-gradient(135deg, ${VERT} 0%, #2E7D32 100%)`, padding:'18px 20px 22px', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:-20, right:-20, width:120, height:120, background:'rgba(255,255,255,0.04)', borderRadius:'50%', pointerEvents:'none' }} />

          {/* Bouton retour employé */}
          {estEmploye && (
            <button onClick={() => navigate('/accueil-employe')}
              style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:8, padding:'4px 10px', fontSize:'0.72rem', cursor:'pointer', marginBottom:10, fontWeight:600, display:'inline-flex', alignItems:'center', gap:5 }}>
              ← Accueil
            </button>
          )}

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', position:'relative', zIndex:1 }}>
            <div>
              <p style={{ margin:'0 0 3px', color:'#fff', fontWeight:700, fontSize:'1rem', display:'flex', alignItems:'center', gap:8 }}><Umbrella size={18} />Gestion des Congés</p>
              <p style={{ margin:0, color:'rgba(255,255,255,0.7)', fontSize:'0.7rem' }}>
                {isRH ? 'Vue RH — tous les employés' : peutValiderConges ? 'Vos demandes + validation équipe' : 'Vos demandes de congé'}
              </p>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {isRH && (
                <button onClick={() => setShowStats(s => !s)}
                  style={{ background:'rgba(255,255,255,0.15)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', borderRadius:10, padding:'8px 12px', fontWeight:600, fontSize:12, cursor:'pointer' }}>
                  <BarChart2 size={14} style={{ marginRight:5 }} />{showStats ? 'Masquer stats' : 'Statistiques'}
                </button>
              )}
              <button className="sg-principal" onClick={() => setModaleNew(true)}
                style={{ background:'rgba(255,255,255,0.2)', color:'#fff', border:'1px solid rgba(255,255,255,0.4)', borderRadius:10, padding:'9px 16px', fontWeight:700, fontSize:13, cursor:'pointer', backdropFilter:'blur(4px)' }}>
                + Nouvelle demande
              </button>
              <NotificationBell couleurIcone="#fff" taille={17} />
            </div>
          </div>
        </div>

        <div style={{ padding:'14px 16px 0' }}>
          {/* Solde */}
          <CarteSolde solde={solde} />

          {/* Dashboard stats RH */}
          {isRH && showStats && <div style={{ marginTop:14 }}><DashboardRH stats={stats} /></div>}

          {/* Onglets */}
          <div style={{ marginTop:14 }}>
            <div style={{ display:'flex', gap:4, borderBottom:`2px solid ${GRIS}`, marginBottom:16 }}>
              {onglets.map(o => (
                <button key={o.id} onClick={() => setOnglet(o.id)}
                  style={{ padding:'8px 16px', border:'none', background:'none', fontWeight:onglet===o.id ? 700 : 400, color:onglet===o.id ? VERT : '#888', borderBottom:onglet===o.id ? `3px solid ${VERT}` : '3px solid transparent', cursor:'pointer', fontSize:13, fontFamily:'inherit', transition:'all 0.15s', marginBottom:-2 }}>
                  {o.label}
                </button>
              ))}
            </div>

            {/* ── Mes demandes ─────────────────────────────── */}
            {onglet === 'mes_demandes' && (
              <div>
                {loading && <p style={{ textAlign:'center', color:'#aaa', fontSize:13 }}>Chargement…</p>}
                {!loading && mesDemandes.length === 0 && (
                  <div style={{ textAlign:'center', padding:'40px 20px', color:'#bbb' }}>
                    <div style={{ marginBottom:10 }}><Inbox size={48} color="#ddd" /></div>
                    <p style={{ fontSize:14, margin:0 }}>Aucune demande de congé</p>
                    <p style={{ fontSize:12, margin:'6px 0 0' }}>Cliquez sur "+ Nouvelle demande" pour en créer une</p>
                  </div>
                )}
                {mesDemandes.map(conge => (
                  <LigneDemande key={conge.id} conge={conge} showEmploye={false} actions={actionsSalarie(conge)} onHistorique={voirHistorique} suivi={suivisMoi.get(String(conge.id))} />
                ))}
              </div>
            )}

            {/* ── À valider ─────────────────────────────────── */}
            {onglet === 'a_traiter' && peutValiderConges && (
              <div>
                {perimetreATraiter && (
                  <div style={{ fontSize:12.5, color:'#33691E', background:'#F1F8E9', border:'1px solid #DCEDC8', borderRadius:8, padding:'8px 12px', marginBottom:12 }}>
                    Demandes de : <b>{perimetreATraiter}</b>. Vos propres demandes ne figurent jamais ici : elles sont traitées par votre responsable ou les RH.
                  </div>
                )}
                {loading && <p style={{ textAlign:'center', color:'#aaa', fontSize:13 }}>Chargement…</p>}
                {!loading && aTraiter.length === 0 && (
                  <div style={{ textAlign:'center', padding:'40px 20px', color:'#bbb' }}>
                    <div style={{ marginBottom:10 }}><CheckCircle size={48} color="#C8E6C9" /></div>
                    <p style={{ fontSize:14, margin:0 }}>Aucune demande à traiter</p>
                  </div>
                )}
                {aTraiter.map(conge => (
                  <LigneDemande key={conge.id} conge={conge} showEmploye={true}
                    actions={getActions(conge)} onHistorique={voirHistorique} suivi={conge.suivi}
                  />
                ))}
              </div>
            )}

            {/* ── Vue globale (RH) ─────────────────────────── */}
            {onglet === 'toutes' && isRH && (
              <div>
                {/* Filtre statut */}
                <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                  {['','en_attente','validee_n1','validee_finale','annulation_demandee','refusee','annulee','interrompue'].map(s => (
                    <button key={s} onClick={() => setFiltreStatut(s)}
                      style={{ padding:'5px 14px', borderRadius:20, border:`1px solid ${filtreStatut===s ? VERT : GRIS}`, background:filtreStatut===s ? VERT : '#fff', color:filtreStatut===s ? '#fff' : '#555', fontWeight:filtreStatut===s ? 700 : 400, fontSize:12, cursor:'pointer' }}>
                      {s === '' ? 'Tous' : STATUT_CFG[s]?.chipLabel || STATUT_CFG[s]?.label || s}
                    </button>
                  ))}
                </div>

                {loading && <p style={{ textAlign:'center', color:'#aaa', fontSize:13 }}>Chargement…</p>}

                {/* Compteurs */}
                {!loading && toutes.length > 0 && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:16 }}>
                    {STATUTS_COMPTEURS.map((k) => [k, STATUT_CFG[k]]).map(([k,cfg]) => {
                      const n = toutes.filter(c => c.statut === k).length;
                      return (
                        <div key={k} style={{ background:'#fff', borderRadius:10, padding:'10px 12px', textAlign:'center', border:`1px solid ${GRIS}`, cursor:'pointer' }}
                          onClick={() => setFiltreStatut(filtreStatut === k ? '' : k)}>
                          <p style={{ margin:0, fontSize:20, fontWeight:800, color:cfg.color }}>{n}</p>
                          <p style={{ margin:0, fontSize:10, color:'#888', textTransform:'uppercase', letterSpacing:0.8 }}>{cfg.label}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!loading && toutes.length === 0 && (
                  <div style={{ textAlign:'center', padding:'40px 20px', color:'#bbb' }}>
                    <p style={{ fontSize:14, margin:0 }}>Aucune demande trouvée</p>
                  </div>
                )}

                {toutes.map(conge => (
                  <LigneDemande key={conge.id} conge={conge} showEmploye={true}
                    actions={getActions(conge)} onHistorique={voirHistorique}
                  />
                ))}
              </div>
            )}
          </div>

          <div style={{ height:32 }} />
        </div>
      </div>
    </Layout>
  );
}
